import React from 'react';
import { FieldArrayWithId, useForm, useWatch } from 'react-hook-form';
import { HStack, IconButton, Text, DataList, DataItem } from '@cad-arc';
import { EventNotificationsEntityIdProvider } from '../../../../event-notifications/event-notifications-entity-id-context';
import { EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import { UseTrackedFieldArrayRemove } from '../../../../forms/use-tracked-field-array';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { EntityPanelFormPersonFieldset } from '../../../../panels/entity-panels/forms/persons/entity-panel-person-fields';
import { useOpenEventPanel } from '../../../use-open-event-panel';
import { EntityPanelFormPersonBaseKey } from '../../../../panels/entity-panels/forms/types';
import { useEventMetadataStore } from '../../event-metadata';

export type EventPanelPersonFieldsetProps = {
    field: FieldArrayWithId<EventPanelFormType, 'reportingParties', 'rhfKey'>;
    index: number;
    remove: UseTrackedFieldArrayRemove;
    baseKey: EntityPanelFormPersonBaseKey;
    allowMembersControl?: boolean;
    // Hide the DEx Fields and use different expansion icon
    displayAsCards?: boolean;
    isReadOnly?: boolean;
    eventPanelOptions: EventPanelOptions;
    userManuallyAddedField?: boolean;
};

export function EventPanelFormPersonFieldset(
    props: EventPanelPersonFieldsetProps
): React.ReactElement {
    const {
        index,
        field,
        remove,
        eventPanelOptions,
        baseKey,
        displayAsCards,
        isReadOnly,
        allowMembersControl,
        userManuallyAddedField,
    } = props;
    const form = useForm<EventPanelFormType>();
    const openEventPanel = useOpenEventPanel();
    const { isFirstResponder } = useApplicationType();
    const isMergedPerson =
        props.baseKey === 'mergedReportingParties' || props.baseKey === 'mergedInvolvedPersons';

    const eventId = field?.eventInfo?.id;

    const onOpenMergedEvent = () => {
        if (eventId) {
            openEventPanel({
                eventId: field?.eventInfo?.id ?? undefined,
            });
        }
    };

    const contextId = useWatch({
        control: form.control,
        name: `${props.baseKey}.${index}.contextId`,
    });

    const eventNumber = useEventMetadataStore().state?.metadata?.agencyEventNumber;

    return (
        <EventNotificationsEntityIdProvider entityId={contextId ?? undefined}>
            {/* Only show the merged text if these are actually merged reporting parties */}
            {field.eventInfo?.isChildEvent && isMergedPerson && !isFirstResponder ? (
                <DataList variant="stacked">
                    <DataItem dataKey="Merged Event">
                        <HStack>
                            <Text>{field?.eventInfo?.agencyEventNumber}</Text>
                            <IconButton
                                size="sm"
                                variant="ghost"
                                aria-label="open event"
                                icon="OpenNewWindow"
                                onClick={onOpenMergedEvent}
                            />
                        </HStack>
                    </DataItem>
                </DataList>
            ) : undefined}
            <EntityPanelFormPersonFieldset
                entityPanelOptions={eventPanelOptions}
                baseKey={baseKey}
                index={index}
                remove={remove}
                displayAsCards={displayAsCards}
                isReadOnly={isReadOnly}
                allowMembersControl={allowMembersControl}
                entityNumber={eventNumber}
                eventId={eventId ?? undefined}
                userManuallyAddedField={userManuallyAddedField}
            />
        </EventNotificationsEntityIdProvider>
    );
}
